import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { MatomoTrackerService, TrackingErrorCategory, TrackingService, trackingErrorCategory } from 'analytics';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { Observable, from } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { LegalDocument, Logger, User } from 'shared';

import { environment } from '../../../environments/environment';
import { CheckoutStartResponse, FunnelService } from '../../../services/funnel.service';
import {
  FAMILY_PLAN_SLUG,
  FunnelIntent,
  PLAN_CODE_BY_SLUG,
  clearFamilyToken,
  currentFamilyToken,
  isFamilyCode,
  markFamilyAutoAccept,
  rememberFamilyToken,
  SESSION_SPONSOR_KEY,
  normaliseIntent,
  readSiteReferral,
  readStoredIntent,
  rememberReferral,
  resolveSponsor,
  returnHereAfterLogin,
  storeIntent,
  validSponsorId,
  wizardQuery
} from '../../../services/funnel-intent';
import { PRODUCT_CODES, PRODUCT_PRICES, ProductKey, productKeyOf, productLabel } from '../../../services/product-label';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'WolfPack Membership Funnel';
/** Tracking Engine V1 page id of the app checkout (docs/tracking/EVENT_CONTRACT_V1.md §2). */
const TRACK_PAGE = 'gh-checkout';
const MIN_WEEKS = 1;
const MAX_WEEKS = 39;

/** A membership's duration option. The SPECIAL has none ('once'). */
type Rhythm = 'week' | 'year' | 'once';

/**
 * 'redirect'  — no plan in the link: off to the website (production)
 * 'choose'    — no plan, dev server: a minimal product choice so /r never dead-ends
 * 'checkout'  — the purchase screen
 * 'existing'  — the e-mail belongs to an account: sign in / use the mailed link
 * 'forwarding' — signed in already, or the account was just created: on to the wizard
 * 'family_missing' — the DNA Family SPECIAL was asked for without an invitation token
 */
type Mode = 'redirect' | 'choose' | 'checkout' | 'existing' | 'forwarding' | 'family_missing';

/** Error codes of POST /funnel/checkout/start that have their own message. */
const KNOWN_ERRORS = ['name_required', 'email_invalid', 'captcha_required', 'captcha_failed',
                      'plan_invalid', 'consents_missing', 'consents_stale'];

/** DNA Family SPECIAL refusals of POST /funnel/checkout/start (`family_join.errors.<code>`). */
const FAMILY_ERRORS = ['self_invitation', 'already_bundle_owner', 'practitioner_active', 'invitation_expired',
                       'invitation_revoked', 'invitation_used', 'invitation_invalid', 'family_quota_exhausted',
                       'family_invitation_required', 'invitation_already_accepted'];

/**
 * Direct purchase without a prior registration (customer decision 2026-10-02, A.2):
 *
 *   product -> duration option -> name, e-mail, legal consents -> payment
 *
 * The account is created from what is entered here (POST /funnel/checkout/start,
 * which also sets the soft-login cookie); the wizard then takes the payment. No
 * magic link, no password before buying — the confirmation mail follows, and the
 * dashboard reminds the buyer to confirm (email_verification_deferred).
 *
 * Served on /:lang/r and /:lang/r/:sponsorId. With `plan` (the website's hand-over)
 * it is the purchase screen; without one (an old partner link) it forwards to the
 * website, carrying the referral. A visitor who is already signed in never sees the
 * form: the wizard opens with the plan preselected and the referral travels with
 * the wizard's checkout.
 */
@Component({
  selector: 'app-funnel-direct-checkout-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-direct-checkout-page.component.html',
  styleUrls: ['./funnel-direct-checkout-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelDirectCheckoutPageComponent {
  private http       = inject(HttpClient);
  private router     = inject(Router);
  private route      = inject(ActivatedRoute);
  private funnel     = inject(FunnelService);
  private auth       = inject(AuthService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private matomo     = inject(MatomoTrackerService);
  private tracking   = inject(TrackingService);
  private destroyRef = inject(DestroyRef);
  private logger     = new Logger('FunnelDirectCheckoutPage');
  /** form_interaction 'start' is sent once, on the first focus inside the form. */
  private formStarted = false;

  private shell = viewChild(FunnelShellComponent);

  readonly paymentTestMode = environment.paymentTestMode === true;
  readonly prices = PRODUCT_PRICES;

  mode = signal<Mode>('forwarding');

  /** Newest referral (last touch). Shown as "referred by" and sent with the purchase. */
  sponsorId = signal<string | null>(null);
  /** journey / goal from the website — personalisation, carried into the order. */
  intent: FunnelIntent = {};
  /** DNA Family SPECIAL: the invitation token (`?family=`, or remembered by this tab). */
  familyToken = signal<string | null>(null);
  isFamily = computed(() => this.product() === 'dna_family_special');

  // ----- Product + duration -------------------------------------------------
  product = signal<ProductKey>('membership');
  rhythm  = signal<Rhythm>('week');
  weeks   = signal<number>(1);
  readonly weekOptions = Array.from({ length: MAX_WEEKS - MIN_WEEKS + 1 }, (_, i) => i + MIN_WEEKS);

  /** The plan code that is being bought right now. */
  planCode = computed(() => {
    const codes = PRODUCT_CODES[this.product()];
    return (this.rhythm() === 'week' ? codes.week : this.rhythm() === 'year' ? codes.year : codes.once) ?? '';
  });

  /** The website slug the backend expects (`plan`). */
  planSlug = computed(() => {
    const code = this.planCode();
    if (code === 'GH_DNA_HEALTH_SPECIAL') return 'dna_health_special';
    if (isFamilyCode(code)) return FAMILY_PLAN_SLUG;
    return Object.keys(PLAN_CODE_BY_SLUG).find(slug => PLAN_CODE_BY_SLUG[slug] === code) ?? '';
  });

  weekPrice = computed(() => this.product() === 'practitioner' ? PRODUCT_PRICES.practitioner.week : PRODUCT_PRICES.membership.week);
  yearPrice = computed(() => this.product() === 'practitioner' ? PRODUCT_PRICES.practitioner.year : PRODUCT_PRICES.membership.year);

  total = computed(() => {
    switch (this.rhythm()) {
      case 'week': return Math.round(this.weekPrice() * this.weeks() * 100) / 100;
      case 'year': return this.yearPrice();
      default:     return this.isFamily() ? PRODUCT_PRICES.dna_family_special.once : PRODUCT_PRICES.dna_health_special.once;
    }
  });

  /** "WolfPack Global Health Membership — 12 weeks". */
  label(): string {
    this.languageTick();
    return productLabel(this.translate, this.planCode(), this.rhythm() === 'week' ? this.weeks() : null);
  }

  // ----- Buyer + consents ---------------------------------------------------
  firstName = '';
  lastName  = '';
  email     = '';
  /** Re-evaluates the form-derived computeds (plain fields + ngModel). */
  private formTick = signal(0);
  onInput(): void { this.formTick.update(n => n + 1); }

  documents        = signal<LegalDocument[]>([]);
  documentsLoading = signal(false);
  documentsError   = signal(false);
  checked          = signal<Record<string, boolean>>({});
  /** Documents the backend reported as missing (422 consents_missing). */
  missing          = signal<string[]>([]);

  captchaToken   = signal<string | null>(null);
  captchaError   = signal(false);
  mcaptchaConfig = signal<{ widgetUrl: string; sitekey: string } | null>(null);

  loading = signal(false);
  error   = signal<string | null>(null);

  /** existing_account: whether the account already has a password. */
  hasPassword = signal(false);

  private languageTick = signal(0);

  private static readonly EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  nameValid  = computed(() => { this.formTick(); return this.firstName.trim().length >= 1 && this.lastName.trim().length >= 1; });
  emailValid = computed(() => { this.formTick(); return FunnelDirectCheckoutPageComponent.EMAIL.test(this.email.trim()); });
  consentsComplete = computed(() =>
    this.documents().length > 0 &&
    this.documents().every(doc => !doc.required || this.checked()[doc.key])
  );
  canSubmit = computed(() =>
    this.nameValid() && this.emailValid() && this.consentsComplete() &&
    !!this.captchaToken() && !this.loading() && !!this.planSlug()
  );

  constructor() {
    const params = this.route.snapshot.paramMap;
    const query  = this.route.snapshot.queryParamMap;

    // DNA Family SPECIAL: the invitation token travels as ?family= and is kept for
    // this tab. A family purchase ignores every referral (the inviting main account
    // becomes the sponsor of a new account — set by the server).
    const familyPlan = (query.get('plan') ?? '').trim().toLowerCase() === FAMILY_PLAN_SLUG;
    rememberFamilyToken(query.get('family'));
    this.familyToken.set(familyPlan ? currentFamilyToken(query.get('family')) : null);

    // Referral — last touch: a link in hand (path or ?ref) overwrites what this tab
    // and the site remembered; without one, the newest stored referral applies.
    const explicit = validSponsorId(params.get('sponsorId')) ?? validSponsorId(query.get('ref'));
    let fromSession: string | null = null;
    try { fromSession = validSponsorId(sessionStorage.getItem(SESSION_SPONSOR_KEY)); } catch { /* ignore */ }
    const sponsor = resolveSponsor({
      path:    validSponsorId(params.get('sponsorId')),
      query:   validSponsorId(query.get('ref')),
      session: fromSession,
      site:    readSiteReferral()
    });
    this.sponsorId.set(familyPlan ? null : sponsor);
    if (familyPlan) { /* a family link never touches the stored referral */ }
    else if (explicit) rememberReferral(explicit);
    else if (sponsor) { try { sessionStorage.setItem(SESSION_SPONSOR_KEY, sponsor); } catch { /* ignore */ } }

    const fromUrl = normaliseIntent({
      plan: query.get('plan'), weeks: query.get('weeks'),
      journey: query.get('journey'), goal: query.get('goal')
    });
    const stored = readStoredIntent();
    this.intent = {
      ...fromUrl,
      journey: fromUrl.journey ?? stored.journey ?? null,
      goal:    fromUrl.goal ?? stored.goal ?? null
    };
    storeIntent(this.intent);

    if (fromUrl.plan === FAMILY_PLAN_SLUG && !this.familyToken()) {
      // The family package exists only through an invitation link.
      this.product.set('dna_family_special');
      this.rhythm.set('once');
      this.mode.set('family_missing');
    } else if (fromUrl.plan) {
      this.applySlug(fromUrl.plan, fromUrl.weeks ?? null);
      this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'website_handover', name: fromUrl.plan });
      this.enterWithPlan();
    } else if (environment.production) {
      // An old partner link (/r/<id>) without a product: the website is where the
      // product is chosen. A full page load — `/` is the static site, not an app route.
      this.mode.set('redirect');
      window.location.href = sponsor ? `/?ref=${encodeURIComponent(sponsor)}` : '/';
    } else {
      // Dev server: `/` is the app shell here, so offer the products directly.
      this.mode.set('choose');
    }

    effect(() => { if (this.mode() === 'checkout' && !this.mcaptchaConfig()) this.loadMcaptchaConfig(); });
    effect(() => { if (this.mode() === 'checkout' && this.mcaptchaConfig()) setTimeout(() => this.loadMcaptchaWidget()); });

    const onMessage = this.handleMcaptchaMessage.bind(this);
    window.addEventListener('message', onMessage);
    this.destroyRef.onDestroy(() => window.removeEventListener('message', onMessage));

    this.i18n.currentLanguage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.languageTick.update(n => n + 1));
  }

  // ----- Product choice -----------------------------------------------------

  private applySlug(slug: string, weeks: number | null): void {
    const code = PLAN_CODE_BY_SLUG[slug];
    const product = productKeyOf(code) ?? 'membership';
    this.product.set(product);
    if (product === 'dna_health_special' || product === 'dna_family_special') {
      this.rhythm.set('once');
    } else {
      this.rhythm.set(code === PRODUCT_CODES[product].year ? 'year' : 'week');
      if (weeks) this.weeks.set(weeks);
    }
  }

  /** Dev-only product choice (no plan in the link). */
  chooseProduct(product: ProductKey): void {
    this.product.set(product);
    this.rhythm.set(product === 'dna_health_special' ? 'once' : 'week');
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'product_chosen', name: product });
    this.enterWithPlan();
  }

  setRhythm(rhythm: Rhythm): void { this.rhythm.set(rhythm); }

  setWeeks(value: number | string): void {
    const weeks = Math.round(Number(value));
    if (!Number.isFinite(weeks)) return;
    this.weeks.set(Math.min(MAX_WEEKS, Math.max(MIN_WEEKS, weeks)));
  }

  /** A signed-in visitor goes straight to the wizard; everyone else buys here. */
  private enterWithPlan(): void {
    // Simplification (docs/tracking/PAGE_IDS.md): the source page is inferred from
    // the plan — only the SPECIAL page links the SPECIAL, everything else is the
    // homepage family.
    const slug = this.planSlug();
    this.tracking.checkoutStarted(TRACK_PAGE, slug, slug === 'dna_health_special' ? 'gh-dna-health-special' : 'gh-index');
    this.auth.getUser$().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      if (user) {
        this.mode.set('forwarding');
        // Signed in with a family link: the invitation is accepted on its landing
        // (one click), which then opens the wizard with the family product.
        const familyToken = this.isFamily() ? this.familyToken() : null;
        if (familyToken) {
          this.router.navigate(['/', this.lang(), 'family', 'join', familyToken], { replaceUrl: true });
          return;
        }
        this.router.navigate(['/', this.lang(), 'membership', 'apply'], {
          queryParams: wizardQuery(this.planCode(), this.rhythm() === 'week' ? this.weeks() : null),
          replaceUrl: true
        });
        return;
      }
      this.mode.set('checkout');
      this.loadDocuments();
      this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'checkout_view', name: this.planSlug() });
      this.tracking.checkoutStep(TRACK_PAGE, 'account', 'view');
    });
  }

  // ----- Legal documents ----------------------------------------------------

  loadDocuments(): void {
    this.documentsLoading.set(true);
    this.documentsError.set(false);
    this.funnel.getCheckoutLegal(this.lang())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.documents.set(res.documents ?? []);
          // Never pre-ticked: every consent must be given actively.
          this.checked.set({});
          this.missing.set([]);
          this.documentsLoading.set(false);
        },
        error: err => {
          this.logger.error('failed to load checkout documents', err);
          this.documentsError.set(true);
          this.documentsLoading.set(false);
        }
      });
  }

  toggle(key: string, value: boolean): void {
    this.checked.update(current => ({ ...current, [key]: value }));
    if (value) this.missing.update(list => list.filter(k => k !== key));
  }

  openDocument(doc: LegalDocument, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.shell()?.openDocument(doc.url);
  }

  // ----- mCaptcha (same widget logic as the join page) -----------------------

  private loadMcaptchaConfig(): void {
    this.http.get<{ widgetUrl: string; sitekey: string }>('/auth/mcaptcha_config')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: cfg => this.mcaptchaConfig.set(cfg),
        error: err => {
          this.logger.error('failed to load mcaptcha config', err);
          this.error.set(this.translate.translate('funnel.checkout.errors.captcha_unavailable'));
        }
      });
  }

  private loadMcaptchaWidget(): void {
    const cfg = this.mcaptchaConfig();
    const container = document.getElementById('mcaptcha-widget');
    if (!cfg || !container) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${cfg.widgetUrl}?sitekey=${cfg.sitekey}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '95px';
    iframe.style.overflow = 'hidden';
    iframe.title = 'mCaptcha verification';
    iframe.setAttribute('scrolling', 'no');

    container.innerHTML = '';
    container.appendChild(iframe);
  }

  private handleMcaptchaMessage(event: MessageEvent): void {
    const widgetUrl = this.mcaptchaConfig()?.widgetUrl;
    if (!widgetUrl) return;
    let expectedOrigin: string;
    try { expectedOrigin = new URL(widgetUrl).origin; } catch { return; }
    if (event.origin !== expectedOrigin) return;

    const data = event.data;
    let token: string | null = null;
    if (typeof data === 'string')   token = data;
    else if (data?.token)           token = data.token;
    else if (data?.m_captcha_token) token = data.m_captcha_token;

    if (token) {
      this.captchaToken.set(token);
      this.captchaError.set(false);
    }
  }

  /** A captcha token is single-use: after any answer it has to be solved again. */
  private resetCaptcha(): void {
    this.captchaToken.set(null);
    this.loadMcaptchaWidget();
  }

  // ----- Submit ---------------------------------------------------------------

  /** First focus inside the account form (tracking only, no field values). */
  onFormFocus(): void {
    if (this.formStarted) return;
    this.formStarted = true;
    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'start');
  }

  submit(): void {
    this.onInput();
    if (!this.captchaToken()) this.captchaError.set(true);
    if (!this.canSubmit()) {
      if (!this.loading()) {
        this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'error', this.captchaToken() ? 'validation' : 'captcha');
      }
      return;
    }
    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'submit');
    this.tracking.checkoutStep(TRACK_PAGE, 'account', 'submit');

    this.loading.set(true);
    this.error.set(null);
    this.missing.set([]);

    this.funnel.startCheckout({
      first_name:    this.firstName.trim(),
      last_name:     this.lastName.trim(),
      email:         this.email.trim(),
      language:      this.lang(),
      sponsor_id:    this.sponsorId(),
      plan:          this.planSlug(),
      weeks:         this.rhythm() === 'week' ? this.weeks() : null,
      journey:       this.intent.journey ?? null,
      goal:          this.intent.goal ?? null,
      captcha_token: this.captchaToken(),
      consents:      this.documents()
                       .filter(doc => this.checked()[doc.key])
                       .map(doc => ({ key: doc.key, version: doc.version })),
      ...(this.isFamily() && this.familyToken() ? { family_token: this.familyToken() } : {})
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.onStarted(res),
        error: err => this.onFailed(err)
      });
  }

  private onStarted(res: CheckoutStartResponse): void {
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'checkout_submitted', name: res.status });
    if (res.status === 'existing_account') {
      this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'error', 'exists');
      this.tracking.checkoutStep(TRACK_PAGE, 'account_exists', 'view');
      this.loading.set(false);
      this.hasPassword.set(!!res.has_password);
      this.mode.set('existing');
      this.scrollToTop();
      return;
    }

    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'complete');
    this.mode.set('forwarding');
    // The new account holds the invitation now (bound at creation).
    if (this.isFamily()) clearFamilyToken();
    const next = res.next?.startsWith('/') ? res.next : `/${res.next ?? ''}`;
    this.adoptSession().subscribe({
      next: () => { this.loading.set(false); this.router.navigateByUrl(`/${this.lang()}${next}`, { replaceUrl: true }); },
      // The cookie is set but the client session could not be built: the login
      // page is the way in (the confirmation mail carries a sign-in link too).
      error: () => { this.loading.set(false); this.router.navigate(['/', this.lang(), 'login'], { replaceUrl: true }); }
    });
  }

  private onFailed(err: any): void {
    this.loading.set(false);
    this.resetCaptcha();
    const code: string | undefined = err?.error?.code;
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'checkout_error', name: code ?? String(err?.status ?? '') });
    const category: TrackingErrorCategory = code?.startsWith('captcha') ? 'captcha'
      : code && KNOWN_ERRORS.includes(code) ? 'validation'
      : trackingErrorCategory(err);
    this.tracking.formInteraction(TRACK_PAGE, 'checkout', 'error', category);
    this.tracking.checkoutStep(TRACK_PAGE, 'account', 'error', category);

    if (err?.status === 409 && code === 'consents_stale') {
      // A document changed while the page was open: show the new version and ask again.
      this.error.set(this.translate.translate('funnel.checkout.errors.consents_stale'));
      this.loadDocuments();
      return;
    }
    if (code === 'consents_missing') this.missing.set(err?.error?.missing ?? []);
    if (code && FAMILY_ERRORS.includes(code)) {
      this.error.set(this.translate.translate(`family_join.errors.${code}`));
      return;
    }
    this.error.set(code && KNOWN_ERRORS.includes(code)
      ? this.translate.translate(`funnel.checkout.errors.${code}`)
      : (err?.error?.error || this.translate.translate('funnel.checkout.errors.generic')));
  }

  /** Same as the magic-link landing: turn the soft-login cookie into a client session
   *  (GET /user + AuthService.setAuthState), or authGuard bounces the wizard to /login. */
  private adoptSession(): Observable<unknown> {
    return this.http.get<{ user: User }>('/user').pipe(
      switchMap(res => from(this.auth.setAuthState(res.user, null)))
    );
  }

  // ----- Existing account -----------------------------------------------------

  /** Sign in, then come back to exactly this purchase. */
  signIn(): void {
    // Family: back to the invitation landing, which accepts it after the sign-in.
    const familyToken = this.isFamily() ? this.familyToken() : null;
    if (familyToken) {
      markFamilyAutoAccept(familyToken);
      returnHereAfterLogin(`/${this.lang()}/family/join/${familyToken}`);
      this.router.navigate(['/', this.lang(), 'login']);
      return;
    }
    const query = new URLSearchParams(
      Object.entries(wizardQuery(this.planCode(), this.rhythm() === 'week' ? this.weeks() : null))
        .map(([key, value]) => [key, String(value)])
    ).toString();
    returnHereAfterLogin(`/${this.lang()}/membership/apply?${query}`);
    this.router.navigate(['/', this.lang(), 'login']);
  }

  backToForm(): void {
    this.mode.set('checkout');
    this.loadDocuments();
  }

  private scrollToTop(): void {
    document.querySelector('.funnel-shell')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  lang(): string {
    return this.i18n.getLanguage() || 'en';
  }
}
